<template>
  <App class="documents-page" :class="{ 'documents-page--tools': toolsLayout }">
    <AppBar :closable="!hideBarClose">
      <div class="documents-top-bar">
        <Button
          v-if="toolsLayout"
          class="documents-menu-btn"
          size="small"
          icon="md-menu"
          title="文档目录"
          @click="toggleTreeDrawer"
        />
        <div class="documents-bar-left">
          <BaseIcon icon="md-document" :size="20" color="var(--primary)" />
          <div class="documents-bar-titles">
            <span class="documents-bar-title">{{ rootTitle }}</span>
            
          </div>
        </div>
        <div class="documents-bar-actions">
          <Button
            size="small"
            title="分享目录"
            :icon="isNarrowToolsViewport ? 'md-share' : undefined"
            @click="copyPublicShareUrl"
          >
            <span v-if="!isNarrowToolsViewport">分享</span>
          </Button>
          <Button
            size="small"
            title="分享此页"
            :disabled="!selectedDocId"
            :icon="isNarrowToolsViewport ? 'md-link' : undefined"
            @click="copySingleDocumentShareUrl"
          >
            <span v-if="!isNarrowToolsViewport">分享此页</span>
          </Button>
          <Button v-if="!hideEdit" size="small" icon="md-create" @click="goEditor">编辑</Button>
        </div>
      </div>
    </AppBar>

    <AppMask v-if="pageLoading" />

    <div
      v-if="toolsLayout && isNarrowToolsViewport && treeDrawerOpen"
      class="documents-tree-overlay"
      @click="closeTreeDrawer"
    />

    <AppLeft
      class="documents-app-left"
      :collapse="treePanelCollapsed"
      :width="treePanelWidth"
    >
      <DocumentTree
        :tree="tree"
        :root-id="directoryId"
        :selected-id="selectedDocId"
        :loading="treeLoading"
        :preview-mode="previewMode"
        @select="onTreeDocSelect"
        @dir-action="handleDirAction"
      />
    </AppLeft>

    <AppClient :collapse-left="clientCollapseLeft" :left-width="treePanelWidth" :loading="false">
      <div class="documents-view-body">
        <DocumentContentViewer :doc="currentDoc" :loading="contentLoading" />
      </div>
    </AppClient>
  </App>
</template>

<script>
import App from '@/components/app/app';
import AppBar from '@/components/app/bar';
import AppMask from '@/components/app/mask';
import AppLeft from '@/components/app/left';
import AppClient from '@/components/app/client';
import BaseIcon from '@/components/base/Icon';
import DocumentTree from './DocumentTree.vue';
import DocumentContentViewer from './DocumentContentViewer.vue';
import {
  findSelectableFileById,
  flattenSelectableFiles,
  getDocumentTitle,
  normalizeTreeRoot,
} from './document-helpers';
import documentsToolsLayout from './mixins/documents-tools-layout';
import documentsShare from './mixins/documents-share';

export default {
  name: 'DocumentsView',
  mixins: [documentsToolsLayout, documentsShare],
  props: {
    embedDirId: {
      type: String,
      default: '',
    },
    hideEdit: {
      type: Boolean,
      default: false,
    },
    hideBarClose: {
      type: Boolean,
      default: false,
    },
    usePublicApi: {
      type: Boolean,
      default: false,
    },
    previewMode: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    App,
    AppBar,
    AppMask,
    AppLeft,
    AppClient,
    BaseIcon,
    DocumentTree,
    DocumentContentViewer,
  },
  routerOption: {
    as_object: true,
    as_default: true,
  },
  data() {
    return {
      pageLoading: true,
      treeLoading: false,
      contentLoading: false,
      tree: [],
      rootTitle: '在线文档',
      selectedDocId: '',
      currentDoc: null,
    };
  },
  computed: {
    directoryId() {
      return this.embedDirId || this.$route.params.id;
    },
    breadcrumb() {
      return this.currentDoc ? getDocumentTitle(this.currentDoc) : '';
    },
    rootNodes() {
      return normalizeTreeRoot(this.tree);
    },
  },
  watch: {
    directoryId: {
      handler(id) {
        if (id) this.loadTree();
      },
      immediate: true,
    },
    '$route.query.doc': {
      handler(docId) {
        if (docId) {
          this.selectDocumentById(docId);
        }
      },
    },
  },
  methods: {
    directoriesApiUrl() {
      return this.usePublicApi
        ? `public/kno_dirs/${this.directoryId}`
        : `knowledge/directories/${this.directoryId}`;
    },
    dataApiUrl(id) {
      return this.usePublicApi
        ? `public/kno_data/${id}`
        : `knowledge/data/${id}`;
    },
    withApiAuth(fn) {
      if (this.usePublicApi) {
        fn();
      } else {
        this.$api.afterLogin(fn);
      }
    },
    loadTree() {
      if (!this.directoryId) return;
      this.pageLoading = true;
      this.treeLoading = true;
      this.withApiAuth(() => {
        this.$api.get(this.directoriesApiUrl())
          .then((res) => {
            const data = res.data.data;
            this.tree = data;
            const root = normalizeTreeRoot(data)[0];
            this.rootTitle = (root && root.name) || '在线文档';
            this.pageLoading = false;
            this.treeLoading = false;
            this.initSelection();
          })
          .catch((err) => {
            this.pageLoading = false;
            this.treeLoading = false;
            this.$Message.error(`加载目录失败: ${err}`);
          });
      });
    },
    initSelection() {
      const queryDoc = this.$route.query.doc;
      if (queryDoc) {
        this.selectDocumentById(queryDoc);
        return;
      }
      const files = flattenSelectableFiles(this.rootNodes);
      if (files.length) {
        this.handleDocSelect(files[0], false);
      } else {
        this.selectedDocId = '';
        this.currentDoc = null;
      }
    },
    selectDocumentById(id) {
      const doc = findSelectableFileById(this.rootNodes, id);
      if (doc) {
        this.handleDocSelect(doc, false);
      }
    },
    onTreeDocSelect(doc) {
      this.handleDocSelect(doc, true);
      this.closeTreeDrawer();
    },
    handleDocSelect(doc, pushQuery = true) {
      if (!doc || !doc.id) return;
      this.selectedDocId = doc.id;
      this.loadDocumentContent(doc);
      if (pushQuery) {
        const query = {...this.$route.query, doc: doc.id};
        if (this.$route.query.doc !== String(doc.id)) {
          this.$router.replace({query}).catch(() => {});
        }
      }
    },
    loadDocumentContent(doc) {
      const id = doc && doc.id;
      if (id == null || id === '') {
        this.currentDoc = null;
        this.contentLoading = false;
        return;
      }
      this.contentLoading = true;
      this.currentDoc = null;
      this.withApiAuth(() => {
        this.$api.get(this.dataApiUrl(id))
          .then((res) => {
            const body = res && res.data;
            const data = body && body.data != null ? body.data : body;
            this.currentDoc = data || {};
          })
          .catch((err) => {
            this.currentDoc = null;
            this.$Message.error(`加载文档失败: ${err}`);
          })
          .finally(() => {
            this.contentLoading = false;
          });
      });
    },
    goEditor() {
      if (this.embedDirId) {
        this.$emit('go-editor');
        return;
      }
      this.$router.push({
        path: `/core/documents/${this.directoryId}/editor`,
        query: this.selectedDocId ? {doc: this.selectedDocId} : {},
      });
    },
    handleDirAction(payload) {
      const {action, dir} = payload || {};
      if (action !== 'open-preview' || !dir || dir.id == null) return;
      if (this.embedDirId) {
        this.openToolsDirPreview(dir.id);
        return;
      }
      const route = this.$router.resolve({
        path: `/core/documents/${dir.id}/view`,
      });
      window.open(route.href, '_blank');
    },
    openToolsDirPreview(dirId) {
      const query = {
        dir: String(dirId),
        preview: '1',
      };
      const uid = this.$route.query.uid;
      if (uid) {
        query.uid = uid;
      }
      const noClose = this.$route.query.noClose;
      if (noClose === '1' || noClose === 1 || noClose === 'true') {
        query.noClose = '1';
      }
      const qs = Object.keys(query)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(query[k])}`)
        .join('&');
      window.open(`${window.location.origin}/tools/documents?${qs}`, '_blank');
    },
  },
};
</script>

<style lang="less" scoped>
@import './documents-tools-mobile.less';

.documents-page {
  height: 100%;
  min-height: 100%;
}

.documents-view-body {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.documents-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.documents-bar-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.documents-bar-sep {
  color: var(--subtext3);
}

.documents-bar-sub {
  color: var(--subtext2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;
}

.documents-bar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding-right: 8px;
}
</style>
